import React, { useEffect, useRef, useState } from 'react';
import { VideoCall, CallParticipant } from 'utils/videoCallTypes';
import { useVideoCallStore } from 'utils/videoCallStore';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Phone, 
  PhoneOff, 
  Video, 
  VideoOff, 
  Mic, 
  MicOff, 
  Maximize2, 
  Minimize2,
  Users
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  call: VideoCall;
  currentUser: CallParticipant;
  onEndCall: () => void;
}

export const VideoCallInterface: React.FC<Props> = ({ call, currentUser, onEndCall }) => {
  const {
    showCallInterface,
    setShowCallInterface,
    localStream,
    remoteStreams,
    isVideoEnabled,
    isAudioEnabled,
    toggleVideo,
    toggleAudio,
    isConnecting
  } = useVideoCallStore();
  
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRefs = useRef<{ [userId: string]: HTMLVideoElement | null }>({});
  
  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Get other participants
  const otherParticipants = call.participants
    .filter(p => p !== currentUser.id)
    .map(participantId => call.participantDetails?.[participantId])
    .filter(Boolean) as CallParticipant[];
  
  const primaryParticipant = otherParticipants[0]; // For 1:1 calls
  
  // Determine display name and photo based on user role
  const displayInfo = (() => {
    // Clients always see "TAP Support Team"
    if (currentUser.role === 'client' || currentUser.role === 'limited_access') {
      return {
        name: 'TAP Support Team',
        photoURL: undefined
      };
    }
    
    // Admins/Staff see the actual participant (client) name
    if (primaryParticipant) {
      return {
        name: primaryParticipant.name,
        photoURL: primaryParticipant.photoURL
      };
    }
    
    // Fallback
    return {
      name: 'TAP Support Team',
      photoURL: undefined
    };
  })();

  // Initialize local video
  useEffect(() => {
    if (localStream && localVideoRef.current) {
      const videoEl = localVideoRef.current;
      
      // Set srcObject
      videoEl.srcObject = localStream;
      
      // Wait for metadata to load, then play
      videoEl.onloadedmetadata = () => {
        videoEl.play()
      };
      
      // Also try to play immediately
    }
  }, [localStream]);
  
  // Handle video track enable/disable state changes
  useEffect(() => {
    if (localStream && localVideoRef.current) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        // When video is re-enabled, refresh the srcObject to ensure video plays
        if (isVideoEnabled && videoTrack.enabled) {
          // Re-set srcObject to force video element to refresh
          const currentStream = localVideoRef.current.srcObject;
          localVideoRef.current.srcObject = null;
          localVideoRef.current.srcObject = currentStream;
        }
      }
    }
  }, [localStream, isVideoEnabled]);
  
  // Initialize remote videos
  useEffect(() => {
    Object.entries(remoteStreams).forEach(([userId, stream]) => {
      const videoElement = remoteVideoRefs.current[userId];
      if (videoElement && stream) {
        
        // Set srcObject
        videoElement.srcObject = stream;
        
        // Wait for metadata to load
        videoElement.onloadedmetadata = () => {
          videoElement.play()
        };
        
        // Also try to play immediately
      }
    });
  }, [remoteStreams]);
  
  // Call duration timer
  useEffect(() => {
    if (call.status === 'connected' && call.startedAt) {
      const interval = setInterval(() => {
        const duration = Math.floor((Date.now() - call.startedAt!.getTime()) / 1000);
        setCallDuration(duration);
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [call.status, call.startedAt]);
  
  // Request media permissions when call starts
  useEffect(() => {
    const initializeMedia = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
        // Store stream in the store
        useVideoCallStore.getState().setLocalStream(stream);
      } catch (error) {
        // Error accessing media devices
      }
    };
    
    if (showCallInterface && !localStream) {
      initializeMedia();
    }
  }, [showCallInterface, localStream]);
  
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const getCallTitle = () => {
    if (otherParticipants.length === 1 && primaryParticipant) {
      return displayInfo.name;
    }
    if (otherParticipants.length > 1) {
      return `Group Call (${call.participants.length} participants)`;
    }
    return 'Video Call';
  };
  
  const getCallStatus = () => {
    switch (call.status) {
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return formatDuration(callDuration);
      case 'ringing':
        return 'Ringing...';
      default:
        return 'Calling...';
    }
  };
  
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  
  const remoteParticipants = Object.keys(remoteStreams);
  const hasRemoteVideo = remoteParticipants.length > 0;
  
  return (
    <Dialog open={showCallInterface} onOpenChange={setShowCallInterface}>
      <DialogContent className={cn(
        "p-0 overflow-hidden",
        isMobile 
          ? "max-w-full w-full h-screen max-h-screen m-0 rounded-none" 
          : isFullscreen 
            ? "max-w-full h-screen" 
            : "max-w-4xl h-[600px]"
      )}>
        <div className="relative w-full h-full bg-gray-900">
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/50 to-transparent p-4">
            <div className="flex items-center justify-between text-white">
              <div>
                <h3 className="font-semibold">{getCallTitle()}</h3>
                <p className="text-sm opacity-75">{getCallStatus()}</p>
              </div>
              
              <div className="flex items-center space-x-2">
                {call.participants.length > 2 && (
                  <div className="flex items-center space-x-1 px-2 py-1 bg-black/30 rounded-full">
                    <Users className="h-4 w-4" />
                    <span className="text-sm">{call.participants.length}</span>
                  </div>
                )}
                
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={toggleFullscreen}
                  className="text-white hover:bg-white/20"
                >
                  {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
          
          {/* Main Video Area - Remote Video fills entire screen */}
          <div className="absolute inset-0 z-0">
            {hasRemoteVideo ? (
              // Remote video(s)
              <div className={cn(
                "w-full h-full",
                remoteParticipants.length === 1 ? "" : "grid grid-cols-2 gap-1"
              )}>
                {remoteParticipants.map((userId) => {
                  const participant = call.participantDetails?.[userId];
                  return (
                    <div key={userId} className="relative w-full h-full bg-gray-800">
                      <video
                        ref={(el) => {
                          remoteVideoRefs.current[userId] = el;
                        }}
                        autoPlay
                        playsInline
                        muted={false}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      
                      {/* Participant info overlay */}
                      <div className="absolute bottom-2 left-2 flex items-center space-x-2 bg-black/50 rounded-full px-2 py-1">
                        <Avatar className="h-6 w-6">
                          {participant?.photoURL && <AvatarImage src={participant.photoURL} />}
                          <AvatarFallback className="text-xs">
                            {participant?.name?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-white text-xs">
                          {participant?.name || 'Participant'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // No remote video - show placeholder
              <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                <div className="text-center text-white">
                  <Avatar className="h-24 w-24 mx-auto mb-4">
                    {displayInfo.photoURL && <AvatarImage src={displayInfo.photoURL} />}
                    <AvatarFallback className="bg-gray-600 text-white text-2xl">
                      {displayInfo.name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <p className="text-lg font-medium">
                    {isConnecting ? 'Connecting...' : `Waiting for ${displayInfo.name}`}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Local video (Picture-in-Picture) - Overlaid on top */}
          <div className={cn(
            "absolute bg-gray-700 rounded-lg overflow-hidden border-2 border-white/20 z-20",
            isMobile 
              ? "top-20 right-4 w-24 h-32" 
              : "bottom-4 right-4 w-48 h-36"
          )}>
            {localStream && isVideoEnabled ? (
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover scale-x-[-1]" // Mirror local video
              />
            ) : (
              <div className="w-full h-full bg-gray-600 flex items-center justify-center">
                <Avatar className={cn(isMobile ? "h-12 w-12" : "h-16 w-16")}>
                  <AvatarImage src={currentUser.photoURL} />
                  <AvatarFallback className="bg-gray-500 text-white">
                    {currentUser.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              </div>
            )}
            
            {/* Video disabled indicator */}
            {!isVideoEnabled && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <VideoOff className={cn(isMobile ? "h-4 w-4" : "h-6 w-6", "text-white")} />
              </div>
            )}
            
            {/* Audio muted indicator */}
            {!isAudioEnabled && (
              <div className="absolute top-2 left-2">
                <div className="bg-red-500 rounded-full p-1">
                  <MicOff className={cn(isMobile ? "h-2 w-2" : "h-3 w-3", "text-white")} />
                </div>
              </div>
            )}
          </div>
          
          {/* Controls - Overlaid at bottom */}
          <div className={cn(
            "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent z-30",
            isMobile ? "p-4" : "p-6"
          )}>
            <div className={cn(
              "flex items-center justify-center",
              isMobile ? "space-x-3" : "space-x-4"
            )}>
              {/* Mute/Unmute */}
              <Button
                size="lg"
                onClick={toggleAudio}
                className={cn(
                  "rounded-full relative z-50",
                  isMobile ? "h-14 w-14" : "h-16 w-16",
                  isAudioEnabled 
                    ? "bg-gray-600 hover:bg-gray-700" 
                    : "bg-red-600 hover:bg-red-700"
                )}
              >
                <div className="flex items-center justify-center">
                  {isAudioEnabled ? (
                    <Mic className={cn(isMobile ? "h-6 w-6" : "h-7 w-7", "text-white")} />
                  ) : (
                    <MicOff className={cn(isMobile ? "h-6 w-6" : "h-7 w-7", "text-white")} />
                  )}
                </div>
              </Button>
              
              {/* Video On/Off */}
              <Button
                size="lg"
                onClick={toggleVideo}
                className={cn(
                  "rounded-full relative z-50",
                  isMobile ? "h-14 w-14" : "h-16 w-16",
                  isVideoEnabled 
                    ? "bg-gray-600 hover:bg-gray-700" 
                    : "bg-red-600 hover:bg-red-700"
                )}
              >
                <div className="flex items-center justify-center">
                  {isVideoEnabled ? (
                    <Video className={cn(isMobile ? "h-6 w-6" : "h-7 w-7", "text-white")} />
                  ) : (
                    <VideoOff className={cn(isMobile ? "h-6 w-6" : "h-7 w-7", "text-white")} />
                  )}
                </div>
              </Button>
              
              {/* End Call */}
              <Button
                size="lg"
                onClick={onEndCall}
                className={cn(
                  "rounded-full bg-red-600 hover:bg-red-700",
                  isMobile ? "h-20 w-20" : "h-20 w-20"
                )}
              >
                <PhoneOff className={cn(isMobile ? "h-7 w-7" : "h-8 w-8", "text-white")} />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
